import { useState, useEffect, useCallback } from 'react';
import supabase from '../supabase/supabase';
import { sendCheckoutEmail } from '../services/emailService';
import { format } from 'date-fns';

export const useBooks = (searchQuery = '', filters = {}) => {
  const [books, setBooks] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Internal state for the debounced search term to prevent jitter
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchAllCategories = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('books')
        .select('category');
      
      if (error) throw error;
      
      const uniqueCats = [...new Set(data.map(b => b.category).filter(Boolean))].sort();
      setAllCategories(uniqueCats);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  const fetchBooks = useCallback(async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      const isAuthenticated = !!session?.user;

      const baseFields = `
        id, title, author, isbn, category, tags, description, cover_image, 
        status, total_copies, available_copies, published_year, created_at
      `;

      const loanFields = `
        loans (
          id, due_date, returned_date,
          profiles ( full_name, email )
        )
      `;

      const selectFields = isAuthenticated 
        ? `${baseFields}, ${loanFields}` 
        : baseFields;

      let query = supabase
        .from('books')
        .select(selectFields);

      // Robust Partial Matching Search
      // Replaced textSearch with .or() chain using ilike for partial word matching
      if (debouncedQuery.trim()) {
        const q = debouncedQuery.trim();
        query = query.or(`title.ilike.%${q}%,author.ilike.%${q}%,category.ilike.%${q}%`);
      }

      if (filters.category && Array.isArray(filters.category) && filters.category.length > 0) {
        query = query.in('category', filters.category);
      }

      const { data, error } = await query.order('title');
      if (error) throw error;

      const processedBooks = data.map(book => {
        const available = book.available_copies ?? 0;
        return {
          ...book,
          isAvailable: available > 0 && book.status !== 'maintenance'
        };
      });

      const finalBooks = filters.available 
        ? processedBooks.filter(b => b.isAvailable) 
        : processedBooks;

      setBooks(finalBooks);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, JSON.stringify(filters)]);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  useEffect(() => {
    fetchAllCategories();
  }, [fetchAllCategories]);

  const checkoutBook = async (bookId, userId) => {
    try {
      const { data, error } = await supabase.rpc('rpc_checkout_book', {
        p_book_id: bookId,
        p_user_id: userId
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);

      const { data: userData } = await supabase.from('profiles').select('full_name, email').eq('id', userId).single();
      if (userData?.email) {
        sendCheckoutEmail(userData.email, userData.full_name, data.title, format(new Date(data.due_date), 'MMMM do, yyyy'))
          .catch(err => console.error('Email notification failed', err));
      }

      fetchBooks(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const returnBook = async (bookId, loanId) => {
    try {
      const { data, error } = await supabase.rpc('rpc_return_book', {
        p_loan_id: loanId,
        p_book_id: bookId
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);

      fetchBooks(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const addBook = async (bookData) => {
    try {
      const totalCopies = parseInt(bookData.total_copies) || 1;
      const { data, error } = await supabase
        .from('books')
        .insert([{ ...bookData, available_copies: totalCopies, status: 'available' }])
        .select()
        .single();

      if (error) throw error;
      fetchBooks(true);
      fetchAllCategories();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateBook = async (bookId, bookData) => {
    try {
      const { error } = await supabase.from('books').update(bookData).eq('id', bookId);
      if (error) throw error;
      fetchBooks(true);
      fetchAllCategories();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  return { 
    books, 
    loading, 
    error, 
    addBook, 
    updateBook, 
    checkoutBook, 
    returnBook, 
    dynamicCategories: allCategories,
    refetch: fetchBooks 
  };
};